import { assertUint8Arrays, hashUint8Array, openDB, promisifyRequest } from "./Utils";
import { DB_NAME, DB_VERSION, setupSchema } from "./Schema";
import { encryptMessage, decryptMessage } from "../utils/CryptoHelpers";
import { sha256 } from "js-sha256";
import bs58 from "bs58";

import { MESSAGES_TYPES } from "../Config";

async function getDB() {
  return await openDB(DB_NAME, DB_VERSION, setupSchema);
}

/**
 * Stores an encrypted message in IndexedDB
 * @param {string} userId - User id
 * @param {uint8array} conversationId
 * @param {string} message - The plaintext message
 * @param {number} timestamp - UNIX timestamp
 * @param {string} password - User password (used for encryption)
 */
export async function storeEncryptedMessage({
  conversationId,
  messageJSON,
  userId,
  password,
}) {
  // Validate that all binary fields are Uint8Array
  assertUint8Arrays({ conversationId });

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const saltB64 = btoa(String.fromCharCode(...salt)); // store salt as base64 string
  const userKeyHash = await sha256(userId + password);
  const hashedConversationId = await hashUint8Array(conversationId);

  const { encryptedContent } = await encryptMessage({ 
    message: messageJSON, 
    password, 
    salt, 
  });

  const db = await getDB();
  const tx = db.transaction("messages", "readwrite");
  const store = tx.objectStore("messages");

  return promisifyRequest(store.add({
    userKeyHash,
    hashedConversationId,
    encryptedContent,
    salt: saltB64,
  }));
}

export async function loadDecryptedMessages({ userId, conversationId, password }) {
  // Validate that all binary fields are Uint8Array
  assertUint8Arrays({ conversationId });
  
  const userKeyHash = await sha256(userId + password);
  const hashedConversationId = await hashUint8Array(conversationId);

  const db = await getDB();
  const tx = db.transaction("messages", "readonly");
  const store = tx.objectStore("messages");

  const all = await promisifyRequest(store.getAll());

  const filtered = all.filter(m =>
    m.userKeyHash === userKeyHash &&
    m.hashedConversationId === hashedConversationId
  );

  return Promise.all(filtered.map(async (entry) => {
    const saltBytes = Uint8Array.from(atob(entry.salt), c => c.charCodeAt(0));
    const message = JSON.parse(await decryptMessage(entry.encryptedContent, password, saltBytes));

    return {
      conversationId,
      message: parseMessage({ 
        message: message.message,
        messageType: message.messageType, 
      }),
      messageType: message.messageType,
      read: message.read,
      receiverViewPublicKey: bs58.decode(message.receiverViewPublicKey),
      senderViewPublicKey: bs58.decode(message.senderViewPublicKey),
      timestamp: message.timestamp,
    }
  }));
}

export function parseMessage({ message, messageType }) {
  // MESSAGE_TYPE_HANDSHAKE_REQUEST
  if (messageType == MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_REQUEST) {
    return {
      kyberPublicKey: (message.kyberPublicKey instanceof Uint8Array)? message.kyberPublicKey: bs58.decode(message.kyberPublicKey), // 2do dirty hack
      message: message.message,
      version: message.version,
    }
  }

  // MESSAGE_TYPE_HANDSHAKE_CONFIRM
  else if (messageType == MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_CONFIRM) {
    return {
      kyberCiphertext: (message.kyberCiphertext instanceof Uint8Array)? message.kyberCiphertext: bs58.decode(message.kyberCiphertext), // 2do dirty hack
      version: message.version,
    }
  }

  // MESSAGE_TYPE_MESSAGE_TYPE_TEXT
  else if (messageType == MESSAGES_TYPES.MESSAGE_TYPE_TEXT) {
    return {
      message: message.message,
      version: message.version,
    }
  }

  // MESSAGE_TYPE_MESSAGE_TYPE_TEXT
  else if (messageType == MESSAGES_TYPES.MESSAGE_TYPE_MEDIA) {
    return {
      bytes: (message.bytes instanceof Uint8Array)? message.bytes: bs58.decode(message.bytes), // 2do dirty hack
      fileName: message.fileName,
      mimeType: message.mimeType,
      version: message.version,
    }
  }
  throw Error ('Unknown message type');
}

